import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { filter, tap } from 'rxjs/operators';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    AttributionBudgetModelDto,
    ChampDto, ModeleRegleAccessibiliteEpargnesInput,
    ModeleRegleAccModelDto,
    MoteurRegleTypeDateRefDto,
    RegleAccListeValModelDto
} from 'src/graphql/generated-types';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import {
    SharedAllocationAccessibilityLoadRelatedListAction,
} from 'src/app/_shared/stores/allocation-accessibility/shared-allocation-accessibility.action';
import {
    SharedAllocationAccessibilityState
} from 'src/app/_shared/stores/allocation-accessibility/shared-allocation-accessibility.state';

type SharedAllocationAccessibilityDetail = RegleAccListeValModelDto & { Valeur: string };

@Component({
    selector: 'app-shared-allocation-accessibility-detail',
    templateUrl: './shared-allocation-accessibility-detail.component.html',
    styleUrls: ['./shared-allocation-accessibility-detail.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationAccessibilityDetailComponent<T extends SharedAllocationAccessibilityDetail> extends BaseComponent implements OnInit {

    dataSource: T[] = [];

    filteredBeneficiaryTypesList: string[] = [];

    filteredChildrenAccessibilityCriteriaList: string[] = [];

    referenceDatesList: MoteurRegleTypeDateRefDto[] = [];

    accessibilityCriteriaList: ChampDto[] = [];

    selectedAccessibilityCriteria: ChampDto = null;

    columns: KalTableHeader<T>[] = [
        {label: 'Valeur', code: 'Valeur'},
        {label: 'Ancienneté Min en mois', code: 'AncienneteMinimum'},
        {label: 'Ancienneté Max en mois', code: 'AncienneteMaximum'},
    ];

    formControl = new UntypedFormControl({value: 'accessibilityCriteria', disabled: true});

    @Input()
    model: ModeleRegleAccessibiliteEpargnesInput = null;

    constructor(protected readonly cdr: ChangeDetectorRef) {
        super(cdr);
    }

    private _allocation$ = new BehaviorSubject<AttributionBudgetModelDto>(null);

    get allocation$(): Observable<AttributionBudgetModelDto> {
        return this._allocation$.asObservable();
    }

    get allocation(): AttributionBudgetModelDto {
        return this._allocation$.getValue();
    }

    @Input()
    set allocation(allocation: AttributionBudgetModelDto) {
        this._allocation$.next(allocation);
    }

    get isAnciennete(): boolean {
        return this.accessibilityRule.ModeAccess === 'ANC';
    }

    get isForcageAutorise(): boolean {
        return this.accessibilityRule?.IsForcageAutorise;
    }

    get accessibilityRule(): ModeleRegleAccModelDto {
        return this.allocation.MoteurRegleAcc?.ModeleRegleAcc;
    }

    get referenceDate(): MoteurRegleTypeDateRefDto {
        return this.referenceDatesList.find(d => d.Id === (this.accessibilityRule.IdtypeRefAnc || this.accessibilityRule.IdTypeDateRefAge));
    }

    get accessibilityRuleTypeLabel(): string {
        return (!this.accessibilityRule || this.isAnciennete) ? 'Ancienneté' : 'Date de présence';
    }

    get selectedDate(): string {
        if (['SAI', 'SAIINF'].some(type => this.referenceDate?.Code === type)) {
            if (!this.accessibilityRule.DateRefJour && !this.accessibilityRule.DateRefMois) {
                return '';
            }
            return this.formatDayMonth(this.accessibilityRule.DateRefJour, this.accessibilityRule.DateRefMois);
        }

        return '';
    }

    get childrenSelectedDate(): string {
        const [day, month] = [
            this.accessibilityRule.DateRefJourAgeEnf || this.accessibilityRule.DateRefJour,
            this.accessibilityRule.DateRefMoisAgeEnf || this.accessibilityRule.DateRefMois
        ];

        return this.formatDayMonth(day, month);
    }

    private formatDayMonth(day: number, month: number): string {
        if (!day && !month) {
            return ' (non renseigné)';
        }

        return ' (' + [day, month].map(e => e.toString().padStart(2, '0')).join('/') + ')';
    }

    get childrenReferenceDate(): MoteurRegleTypeDateRefDto {
        return this.referenceDatesList.find(({Id}) => Id === this.accessibilityRule.IdTypeDateRefAge);
    }

    get beneficiaryDate(): string {
        const minDate = this.accessibilityRule.RgModAccesAnMin;
        const maxDate = this.accessibilityRule.RgModAccesAnMax;

        if (minDate && maxDate) {
            return `De ${minDate} mois à ${maxDate} mois`;
        } else if (minDate) {
            return `A partir de ${minDate} mois d\'ancienneté`;
        } else if (maxDate) {
            return `Jusqu\'à ${maxDate} mois d\'ancienneté`;
        }

        return '';
    }

    get displayedColumns(): (keyof T)[] {
        return this.isAnciennete ? this.columns.map(({code}) => code) : ['Valeur'];
    }

    get hasChildren(): boolean {
        return ['IsEnfantDirect', 'IsEnfantHandicape', 'IsEnfantACharge', 'AgeEnfantMin', 'AgeEnfantMax'].some(type => {
            return !!this.accessibilityRule[type];
        });
    }

    get shouldDisplayBeneficiary(): boolean {
        return this.accessibilityRule.IsConjointHandicape || this.hasChildren;
    }

    private loadRelatedList(): void {
        this.loading = true;

        this.watch$ = this.dispatch$(new SharedAllocationAccessibilityLoadRelatedListAction()).pipe(
            tap(() => {
                this.accessibilityCriteriaList = this.snapshot(SharedAllocationAccessibilityState.accessibilityCriteriaList) || [];
                this.referenceDatesList = this.snapshot(SharedAllocationAccessibilityState.referenceDatesList) || [];

                this.initAllocationForm();
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private initAllocationForm(): void {
        this.watch$ = this.allocation$.pipe(
            filter(e => !!e),
            tap(() => {

                const beneficiaryTypesList = this.snapshot(SharedAllocationAccessibilityState.beneficiaryTypesList);
                this.filteredBeneficiaryTypesList = this.accessibilityRule?.ListeIdTypeBenef?.decimal?.map(typeId => {
                    return beneficiaryTypesList?.find(({Id}) => Id === typeId).Libelle;
                });

                if (!this.accessibilityRule?.IdChampAD) {
                    this.filteredChildrenAccessibilityCriteriaList = [];
                } else {
                    const childrenAccessibilityCriteriaList = this.snapshot(SharedAllocationAccessibilityState.childrenAccessibilityCriteriaList);
                    this.filteredChildrenAccessibilityCriteriaList = childrenAccessibilityCriteriaList.find(e => e.Id === this.accessibilityRule.IdChampAD)
                        ?.ListChampListeValeur?.ChampListeValeurDTO
                        ?.filter(value => this.accessibilityRule?.ListeIdChampAD?.decimal.some(id => id === value.Id))
                        ?.map(value => value.Valeur) || [];
                }

                const isAccesbilityBeneficiary = arrayEmpty(this.accessibilityRule?.ListeModeleRegleAccListeModele?.RegleAccListeValModelDTO)
                this.formControl.patchValue(isAccesbilityBeneficiary ? 'accessibilityBeneficiary' : 'accessibilityCriteria');

                if (isAccesbilityBeneficiary) {
                    return;
                }

                this.selectedAccessibilityCriteria = this.accessibilityCriteriaList.find(e => e.Id === this.accessibilityRule.IdChamp);
                const criteriaValuesList = this.selectedAccessibilityCriteria?.ListChampListeValeur?.ChampListeValeurDTO || [];
                const filteredCriteriaValuesList = criteriaValuesList.filter(v => !v.IsCloture);

                const ruleValuesList = this.accessibilityRule.ListeModeleRegleAccListeModele?.RegleAccListeValModelDTO || [];
                this.dataSource = ruleValuesList
                    .filter(r => filteredCriteriaValuesList?.some(v => v.Id === r.IdValeurChamp))
                    .map(e => ({
                        ...e,
                        Valeur: filteredCriteriaValuesList.find(({Id}) => Id === e.IdValeurChamp).Valeur
                    })) as T[] || [];

                this.cdr.markForCheck();
            })
        );
    }

    ngOnInit(): void {
        this.loadRelatedList();
    }
}
